import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import API, { parseError } from "../utils/api";
import {
  Color,
  Role,
  REGIONLESS_ROLES,
  FIELD_STAFF_ROLES,
} from "../constants";

// ── Shared components ─────────────────────────────────────────────────────────

function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className="px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
          style={
            active === tab.key
              ? { borderColor: Color.NAVY, color: Color.NAVY }
              : { borderColor: "transparent", color: "#6B7280" }
          }
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function Badge({ label, bg, color }) {
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: bg, color }}
    >
      {label}
    </span>
  );
}

function ActionButton({ label, onClick, color = Color.NAVY, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-3 py-1 rounded-lg text-xs font-medium border transition disabled:opacity-40"
      style={{ borderColor: color, color }}
    >
      {label}
    </button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span style={{ color: Color.RED }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2";
const selectCls =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 bg-white";

// ── Import Modal ──────────────────────────────────────────────────────────────

function ImportModal({
  type,
  regionId,
  mustPickRegion,
  regions,
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState("upload");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRegion, setSelectedRegion] = useState(regionId || "");
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("addupdate");
  const [checkedDeactivate, setCheckedDeactivate] = useState({});

  const isStaff = type === "staff";
  const title = isStaff ? "Import Staff" : "Import Customers";

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams();
      if (selectedRegion) params.set("region_id", selectedRegion);
      if (!isStaff) params.set("mode", mode);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const endpoint = isStaff
        ? `/admin/staff/preview-import${qs}`
        : `/admin/customers/preview-import${qs}`;
      const res = await API.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setPreview(res.data);
        setCheckedDeactivate({});
        setStep("preview");
      }
    } catch (err) {
      setError(parseError(err, "Failed to parse file."));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      const params = selectedRegion ? `?region_id=${selectedRegion}` : "";
      const endpoint = isStaff
        ? `/admin/staff/confirm-import${params}`
        : `/admin/customers/confirm-import${params}`;
      const deactivate_ids = Object.entries(checkedDeactivate)
        .filter(([, checked]) => checked)
        .map(([id]) => id);
      const body = { ...preview, deactivate_ids };
      const res = await API.post(endpoint, body);
      setResult(res.data);
      setStep("done");
      onSuccess();
    } catch (err) {
      setError(parseError(err, "Import failed."));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    const res = await API.get(
      `/admin/templates/${isStaff ? "staff" : "customers"}`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = isStaff
      ? "staff_import_template.xlsx"
      : "customers_import_template.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleDeactivate = (id) => {
    setCheckedDeactivate((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const checkedCount = Object.values(checkedDeactivate).filter(Boolean).length;

  return (
    <Modal title={title} onClose={onClose}>
      {/* Region selector — Super Admin only */}
      {mustPickRegion && step === "upload" && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Region <span style={{ color: Color.RED }}>*</span>
          </label>
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className={selectCls}
          >
            <option value="">Select region...</option>
            {regions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── UPLOAD STEP ───────────────────────────────────────────────────── */}
      {step === "upload" && (
        <>
          {/* Mode selector — customers only */}
          {!isStaff && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Import Mode
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: "addupdate",
                    label: "Add / Update",
                    desc: "Add new customers and update existing ones. Doesn't check what's missing.",
                  },
                  {
                    value: "reconcile",
                    label: "Full Reconciliation",
                    desc: "Add / update, plus flag customers in the system that are missing from this file.",
                  },
                ].map((m) => (
                  <div
                    key={m.value}
                    onClick={() => setMode(m.value)}
                    className="p-3 rounded-lg border-2 cursor-pointer text-sm transition"
                    style={{
                      borderColor: mode === m.value ? Color.NAVY : "#D1D5DB",
                      backgroundColor: mode === m.value ? "#EEF0FF" : "white",
                    }}
                  >
                    <p className="font-semibold text-gray-800">{m.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className="mb-4 p-3 rounded-lg text-xs"
            style={{ backgroundColor: "#F8F9FA", border: "1px solid #E5E7EB" }}
          >
            <p className="font-semibold text-gray-600 mb-2">
              Required columns in your Excel file:
            </p>
            {isStaff ? (
              <div className="space-y-1 text-gray-500">
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    name
                  </span>{" "}
                  — Full name <span style={{ color: Color.RED }}>*</span>
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    role
                  </span>{" "}
                  — One of: Account Sales Manager, District Sales Manager,
                  General Sales Manager, Sales Trainee, Sales Activator, Admin{" "}
                  <span style={{ color: Color.RED }}>*</span>
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    email
                  </span>{" "}
                  — Work email (unique key){" "}
                  <span style={{ color: Color.RED }}>*</span>
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    phone
                  </span>{" "}
                  — Phone number
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    route
                  </span>{" "}
                  — Route code (e.g. CCCC01)
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    manager_email
                  </span>{" "}
                  — Manager's email address
                </p>
                <p className="mt-2 text-gray-400 italic">
                  Column order doesn't matter — headers must match exactly.
                </p>
                <p className="mt-2 text-gray-400">
                  If email already exists — record will be updated.
                </p>
              </div>
            ) : (
              <div className="space-y-1 text-gray-500">
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    customer_number
                  </span>{" "}
                  — Unique customer #{" "}
                  <span style={{ color: Color.RED }}>*</span>
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    name
                  </span>{" "}
                  — Store name <span style={{ color: Color.RED }}>*</span>
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    route
                  </span>{" "}
                  — Route code <span style={{ color: Color.RED }}>*</span>
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    address
                  </span>{" "}
                  — Street address
                </p>
                <p>
                  <span className="font-mono bg-gray-200 px-1 rounded">
                    city
                  </span>{" "}
                  — City name
                </p>
                <p className="mt-2 text-gray-400 italic">
                  Column order doesn't matter — headers must match exactly.
                </p>
                <p className="mt-2 text-gray-400">
                  If customer # already exists — record will be updated.
                </p>
              </div>
            )}
          </div>

          <label
            className={`w-full py-8 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition ${
              !mustPickRegion || selectedRegion
                ? "border-gray-300"
                : "border-gray-200 opacity-50 pointer-events-none"
            }`}
          >
            <svg
              className="w-8 h-8 text-gray-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span className="text-sm text-gray-500">
              {loading ? "Parsing..." : "Click to upload Excel file"}
            </span>
            <span className="text-xs text-gray-400 mt-1">.xlsx files only</span>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleFileUpload}
              disabled={loading || (mustPickRegion && !selectedRegion)}
            />
          </label>

          {error && (
            <div
              className="mt-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}
          <p className="text-center mt-3">
            <button
              onClick={downloadTemplate}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              ↓ Download Template
            </button>
          </p>
        </>
      )}

      {/* ── PREVIEW STEP ──────────────────────────────────────────────────── */}
      {step === "preview" && preview && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              {
                label: "New",
                value: preview.will_create,
                bg: "#E8F5E9",
                color: "#005700",
              },
              {
                label: "Update",
                value: preview.will_update || 0,
                bg: "#E3F4FB",
                color: "#0077A3",
              },
              {
                label: "Reactivate",
                value: preview.will_reactivate || 0,
                bg: "#FFF8E1",
                color: "#B38600",
              },
              {
                label: "Skip",
                value: preview.will_skip,
                bg: "#F5F5F5",
                color: "#666",
              },
              {
                label: "Errors",
                value: preview.errors_count,
                bg: "#FDECEA",
                color: "#A00028",
              },
              ...(preview.revise_count > 0
                ? [
                    {
                      label: "Review",
                      value: preview.revise_count,
                      bg: "#FFF3E0",
                      color: "#E65100",
                    },
                  ]
                : []),
            ]
              .filter(
                (s) =>
                  s.value > 0 || ["New", "Skip", "Errors"].includes(s.label),
              )
              .map((s) => (
                <div
                  key={s.label}
                  className="rounded-lg p-2 text-center"
                  style={{ backgroundColor: s.bg, color: s.color }}
                >
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-xs">{s.label}</p>
                </div>
              ))}
          </div>

          {/* Errors */}
          {preview.errors?.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                ERRORS (will be skipped):
              </p>
              {preview.errors.map((e, i) => (
                <div
                  key={i}
                  className="text-xs p-2 rounded mb-1"
                  style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
                >
                  Row {e.row}: {e.reason}
                </div>
              ))}
            </div>
          )}

          {/* Updates */}
          {preview.valid_update?.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                UPDATES:
              </p>
              {preview.valid_update.map((u, i) => (
                <div
                  key={i}
                  className="text-xs p-2 rounded mb-1"
                  style={{ backgroundColor: "#E3F4FB" }}
                >
                  <span className="font-medium">{u.name}</span> —{" "}
                  {Object.entries(u.changes)
                    .map(([f, v]) => `${f}: ${v.from} → ${v.to}`)
                    .join(", ")}
                </div>
              ))}
            </div>
          )}

          {/* Reactivations */}
          {preview.valid_reactivate?.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                WILL REACTIVATE:
              </p>
              {preview.valid_reactivate.map((r, i) => (
                <div
                  key={i}
                  className="text-xs p-2 rounded mb-1"
                  style={{ backgroundColor: "#FFF8E1", color: "#B38600" }}
                >
                  <span className="font-medium">{r.name}</span> —{" "}
                  {r.email || r.customer_number}
                  {Object.keys(r.changes || {}).length > 0 &&
                    ` + ${Object.keys(r.changes).length} field(s) updated`}
                </div>
              ))}
            </div>
          )}

          {/* New records */}
          {preview.valid_new?.length > 0 && (
            <div className="mb-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                NEW RECORDS:
              </p>
              {preview.valid_new.map((r, i) => (
                <div
                  key={i}
                  className="text-xs p-2 rounded mb-1"
                  style={{ backgroundColor: "#E8F5E9" }}
                >
                  {r.name} — {r.email || r.customer_number}
                  {r.role ? ` (${r.role})` : ""}
                </div>
              ))}
            </div>
          )}

          {/* Revise list — reconcile mode only */}
          {preview.revise_list?.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-500">
                  MISSING FROM FILE — REVIEW ({preview.revise_list.length})
                </p>
                {checkedCount > 0 && (
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
                  >
                    {checkedCount} will be deactivated
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-2">
                These customers are active in the system but not in your import
                file. Check the box to deactivate them — their open orders will
                be cancelled.
              </p>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {preview.revise_list.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-start gap-3 p-2.5 hover:bg-gray-50"
                    style={
                      checkedDeactivate[c.id]
                        ? { backgroundColor: "#FDECEA" }
                        : {}
                    }
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 flex-shrink-0"
                      checked={!!checkedDeactivate[c.id]}
                      onChange={() => toggleDeactivate(c.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800">
                        {c.name}
                        <span className="text-gray-400 font-normal ml-1">
                          #{c.customer_number} · {c.route}
                        </span>
                      </p>
                      {c.last_order_date && (
                        <p className="text-xs text-gray-400">
                          Last order: {c.last_order_date}
                        </p>
                      )}
                      {c.open_orders > 0 && (
                        <p
                          className="text-xs font-medium mt-0.5"
                          style={{ color: "#E65100" }}
                        >
                          ⚠ {c.open_orders} open order
                          {c.open_orders > 1 ? "s" : ""} — will be cancelled if
                          deactivated
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-2">
            <button
              onClick={() => {
                setStep("upload");
                setPreview(null);
                setCheckedDeactivate({});
              }}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
            >
              Back
            </button>
            <button
              onClick={handleConfirm}
              disabled={
                loading ||
                (preview.will_create === 0 &&
                  (preview.will_update || 0) === 0 &&
                  (preview.will_reactivate || 0) === 0 &&
                  checkedCount === 0)
              }
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
              style={{
                backgroundColor: checkedCount > 0 ? Color.RED : Color.NAVY,
              }}
            >
              {loading
                ? "Importing..."
                : checkedCount > 0
                  ? `Import + Deactivate ${checkedCount}`
                  : `Import (${preview.will_create} new${preview.will_update ? `, ${preview.will_update} update` : ""}${preview.will_reactivate ? `, ${preview.will_reactivate} reactivate` : ""})`}
            </button>
          </div>
        </>
      )}

      {/* ── DONE STEP ─────────────────────────────────────────────────────── */}
      {step === "done" && result && (
        <div className="text-center py-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: "#E8F5E9" }}
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: "#005700" }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="font-bold text-gray-800 text-lg mb-2">
            Import Complete
          </h3>
          <p className="text-gray-500 text-sm">
            {result.created > 0 && `${result.created} created. `}
            {result.updated > 0 && `${result.updated} updated. `}
            {result.reactivated > 0 && `${result.reactivated} reactivated. `}
            {result.deactivated > 0 && (
              <span style={{ color: Color.RED }}>
                {result.deactivated} deactivated
                {result.orders_cancelled > 0 &&
                  ` (${result.orders_cancelled} orders cancelled)`}
                .
              </span>
            )}
          </p>
          <button
            onClick={onClose}
            className="mt-4 px-6 py-2.5 rounded-xl text-sm font-medium text-white"
            style={{ backgroundColor: Color.NAVY }}
          >
            Done
          </button>
        </div>
      )}
    </Modal>
  );
}

// ── Staff Tab ─────────────────────────────────────────────────────────────────

function StaffTab({ isSuperAdmin, crossRegion, canImport, currentUser }) {
  // Only a Super Admin or GSM may grant admin access — a DSM who has it
  // themselves must not be able to pass it on.
  const canGrantAdmin = isSuperAdmin || currentUser?.role === Role.GSM;

  // Mirrors the backend guards in admin.py so the UI never offers an action
  // that would be rejected:
  //   - Super Admin rows are untouchable for everyone
  //   - a DSM may only manage ASMs and Sales Trainees, even with Grant Admin
  const canManage = (s) => {
    if (s.role === Role.SUPER_ADMIN || s.is_super_admin) return false;
    if (currentUser?.role === Role.DSM) {
      return [Role.ASM, Role.ST].includes(s.role);
    }
    return true;
  };
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [error, setError] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [regions, setRegions] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [confirmToggle, setConfirmToggle] = useState(null); // { item, action: "deactivate"|"activate" }

  const [form, setForm] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    region_id: "",
    route: "",
    manager_id: "",
  });

  const isDsmUser = currentUser?.role === Role.DSM;
  // Super Admin accounts are created and changed directly in the database,
  // so the role is never offered here — not even to a Super Admin.
  const ROLES = isDsmUser
    ? [Role.ASM, Role.ST]
    : Object.values(Role).filter((r) => r !== Role.SUPER_ADMIN);

  useEffect(() => {
    fetchStaff();
    API.get("/lookup/regions")
      .then((r) => setRegions(r.data))
      .catch(() => {});
    API.get("/admin/staff", { params: { is_active: null } })
      .then((r) => setAllStaff(r.data))
      .catch(() => {});
  }, []);

  const fetchStaff = () => {
    setLoading(true);
    API.get("/admin/staff", { params: { is_active: null } })
      .then((r) => {
        setStaff(r.data);
        setAllStaff(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleReactivate = async (s) => {
    if (s.route) {
      const conflict = staff.find(
        (x) => x.is_active && x.route === s.route && x.id !== s.id,
      );
      if (conflict) {
        alert(
          `Note: Route ${s.route} is already assigned to ${conflict.name}. You can still reactivate, but consider updating the route afterward.`,
        );
      }
    }
    try {
      await API.patch(`/admin/staff/${s.id}/toggle-active`);
      fetchStaff();
    } catch {}
  };

  const openCreate = () => {
    setEditingStaff(null);
    setForm({
      name: "",
      role: "",
      email: "",
      phone: "",
      region_id: "",
      route: "",
      manager_id: "",
    });
    setError("");
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditingStaff(s);
    setForm({
      name: s.name,
      role: s.role,
      email: s.email,
      phone: s.phone || "",
      region_id: s.region_id || "",
      route: s.route || "",
      manager_id: s.manager_id || "",
    });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setError("");

    // Mirrors the backend rule: field staff need full contact details.
    const missing = [];
    if (FIELD_STAFF_ROLES.includes(form.role)) {
      if (!form.phone?.trim()) missing.push("Phone");
      // A warehouse-scoped admin has no picker — the backend fills in theirs.
      if (crossRegion && !form.region_id) missing.push("Warehouse");
      if (!form.manager_id) missing.push("Manager");
      if (form.role === Role.ASM && !form.route?.trim()) missing.push("Route");
    }
    if (missing.length) {
      setError(`Required for a ${form.role}: ${missing.join(", ")}`);
      return;
    }

    try {
      // Fix: send null instead of empty string for FK fields
      const payload = {
        ...form,
        manager_id: form.manager_id || null,
        route: form.route || null,
        region_id: form.region_id || null,
        phone: form.phone || null,
      };
      if (editingStaff) {
        await API.patch(`/admin/staff/${editingStaff.id}`, payload);
      } else {
        await API.post("/admin/staff", payload);
      }
      setShowModal(false);
      fetchStaff();
    } catch (err) {
      setError(parseError(err, "Failed to save staff member."));
    }
  };

  const toggleActive = async (s) => {
    try {
      await API.patch(`/admin/staff/${s.id}/toggle-active`);
      fetchStaff();
    } catch {}
  };

  const requestToggleActive = (s) => {
    setConfirmToggle({
      item: s,
      action: s.is_active ? "deactivate" : "activate",
    });
  };

  const toggleAdmin = async (s) => {
    try {
      await API.patch(`/admin/staff/${s.id}/toggle-admin`);
      fetchStaff();
    } catch {}
  };


  const filtered = staff.filter((s) => {
    if (showInactive && s.is_active) return false;
    if (!showInactive && !s.is_active) return false;
    if (roleFilter && s.role !== roleFilter) return false;
    if (regionFilter && s.region_id !== regionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !s.name.toLowerCase().includes(q) &&
        !s.email.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    const av = String(a[sortField] ?? "").toLowerCase();
    const bv = String(b[sortField] ?? "").toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap flex-1">
          <div className="relative flex-1 min-w-48">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
              />
            </svg>
            <input
              type="text"
              placeholder="Search name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputCls} pl-9`}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={selectCls}
            style={{ width: "auto" }}
          >
            <option value="">All Roles</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          {isSuperAdmin && (
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className={selectCls}
              style={{ width: "auto" }}
            >
              <option value="">All Regions</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span style={{ color: showInactive ? Color.RED : undefined }}>
                Show deactivated
              </span>
            </label>
          )}
          {canImport && (
            <button
              onClick={() => setShowImport(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium border flex-shrink-0"
              style={{ borderColor: Color.NAVY, color: Color.NAVY }}
            >
              ↑ Import Excel
            </button>
          )}
          {
            <button
              onClick={openCreate}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex-shrink-0"
              style={{ backgroundColor: Color.NAVY }}
            >
              + Add Staff
            </button>
          }
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {[
                  { field: "name", label: "Name" },
                  { field: "role", label: "Role" },
                  { field: "email", label: "Email", hidden: true },
                  { field: "route", label: "Route", hidden: true },
                  { field: "region_id", label: "Region", hidden: true },
                  { field: "is_active", label: "Status" },
                ].map((col) => (
                  <th
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                    className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none ${col.hidden ? "hidden md:table-cell" : ""}`}
                  >
                    {col.label}
                    {sortField === col.field
                      ? sortDir === "asc"
                        ? " ↑"
                        : " ↓"
                      : " ↕"}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {s.name}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{s.role}</span>
                    {s.is_admin && s.role === Role.DSM && (
                      <span
                        className="ml-1 text-xs px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: "#E8EAF6",
                          color: Color.NAVY,
                        }}
                      >
                        Admin
                      </span>
                    )}

                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {s.email}
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs hidden md:table-cell">
                    {s.route || "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {regions.find((r) => r.id === s.region_id)?.name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      label={s.is_active ? "Active" : "Inactive"}
                      bg={s.is_active ? "#E8F5E9" : "#F5F5F5"}
                      color={s.is_active ? "#005700" : "#666"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5 flex-wrap">
                      {canManage(s) && s.is_active && (
                        <ActionButton
                          label="Edit"
                          onClick={() => openEdit(s)}
                        />
                      )}
                      {canManage(s) &&
                        (s.is_active ? (
                          <ActionButton
                            label="Deactivate"
                            onClick={() => requestToggleActive(s)}
                            color={Color.RED}
                            disabled={s.id === currentUser?.id}
                          />
                        ) : (
                          <ActionButton
                            label="Reactivate"
                            onClick={() => requestToggleActive(s)}
                            color={Color.GREEN}
                          />
                        ))}
                      {s.is_active && s.role === Role.DSM && canGrantAdmin && (
                        <ActionButton
                          label={s.is_admin ? "Revoke Admin" : "Grant Admin"}
                          onClick={() => toggleAdmin(s)}
                          color={s.is_admin ? Color.RED : "#B38600"}
                        />
                      )}

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingStaff ? "Edit Staff Member" : "Add Staff Member"}
          onClose={() => setShowModal(false)}
        >
          <FormField label="Full Name" required>
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </FormField>
          <FormField label="Role" required>
            <select
              className={selectCls}
              value={form.role}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  role: e.target.value,
                  // Regionless roles cover every warehouse
                  region_id: REGIONLESS_ROLES.includes(e.target.value)
                    ? null
                    : f.region_id,
                }))
              }
            >
              <option value="">Select role...</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Email" required>
            <input
              type="email"
              className={inputCls}
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              disabled={!!editingStaff}
            />
          </FormField>
          <FormField
            label="Phone"
            required={FIELD_STAFF_ROLES.includes(form.role)}
          >
            <input
              className={inputCls}
              value={form.phone}
              onChange={(e) =>
                setForm((f) => ({ ...f, phone: e.target.value }))
              }
            />
          </FormField>
          {crossRegion &&
            (REGIONLESS_ROLES.includes(form.role) ? (
              <FormField label="Warehouse">
                <p className="text-sm text-gray-500 py-2">
                  Not applicable — this role covers every warehouse.
                </p>
              </FormField>
            ) : (
              <FormField
                label="Warehouse"
                required={FIELD_STAFF_ROLES.includes(form.role)}
              >
                <select
                  className={selectCls}
                  value={form.region_id || ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      region_id: e.target.value || null,
                    }))
                  }
                >
                  <option value="">Select warehouse...</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </FormField>
            ))}
          {[Role.ASM].includes(form.role) && (
            <FormField label="Route" required>
              <input
                className={inputCls}
                placeholder="e.g. CCCC01"
                value={form.route}
                onChange={(e) =>
                  setForm((f) => ({ ...f, route: e.target.value }))
                }
              />
            </FormField>
          )}
          <FormField
            label="Manager"
            required={FIELD_STAFF_ROLES.includes(form.role)}
          >
            <select
              className={selectCls}
              value={form.manager_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, manager_id: e.target.value }))
              }
            >
              <option value="">No manager</option>
              {allStaff
                .filter(
                  (s) => [Role.DSM, Role.GSM].includes(s.role) && s.is_active,
                )
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.role})
                  </option>
                ))}
            </select>
          </FormField>
          {error && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ backgroundColor: Color.NAVY }}
            >
              {editingStaff ? "Save Changes" : "Add Staff"}
            </button>
          </div>
        </Modal>
      )}
      {showImport && (
        <ImportModal
          type="staff"
          regionId={crossRegion ? null : currentUser?.region_id}
          mustPickRegion={crossRegion}
          regions={regions}
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            fetchStaff();
          }}
        />
      )}

      {/* Deactivate / Reactivate confirmation modal */}
      {confirmToggle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                backgroundColor:
                  confirmToggle.action === "deactivate" ? "#FDECEA" : "#E8F5E9",
              }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  color:
                    confirmToggle.action === "deactivate"
                      ? "#CC0033"
                      : "#008000",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    confirmToggle.action === "deactivate"
                      ? "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                      : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  }
                />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
              {confirmToggle.action === "deactivate"
                ? "Deactivate Staff Member?"
                : "Reactivate Staff Member?"}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {confirmToggle.action === "deactivate" ? (
                <>
                  Are you sure you want to deactivate{" "}
                  <strong>{confirmToggle.item.name}</strong>? They will lose
                  access to the app.
                </>
              ) : (
                <>
                  Are you sure you want to reactivate{" "}
                  <strong>{confirmToggle.item.name}</strong>? They will regain
                  access to the app.
                </>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmToggle(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (confirmToggle.action === "deactivate") {
                    await toggleActive(confirmToggle.item);
                  } else {
                    await handleReactivate(confirmToggle.item);
                  }
                  setConfirmToggle(null);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
                style={{
                  backgroundColor:
                    confirmToggle.action === "deactivate"
                      ? "#CC0033"
                      : "#008000",
                }}
              >
                {confirmToggle.action === "deactivate"
                  ? "Yes, Deactivate"
                  : "Yes, Reactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Customers Tab ─────────────────────────────────────────────────────────────

function CustomersTab({ isSuperAdmin, crossRegion, canImport }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [routeFilter, setRouteFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [error, setError] = useState("");
  const [regions, setRegions] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [confirmToggle, setConfirmToggle] = useState(null);

  const [form, setForm] = useState({
    customer_number: "",
    name: "",
    address: "",
    city: "",
    route: "",
    region_id: "",
  });

  useEffect(() => {
    fetchCustomers();
    API.get("/lookup/regions")
      .then((r) => setRegions(r.data))
      .catch(() => {});
  }, []);

  const fetchCustomers = () => {
    setLoading(true);
    API.get("/admin/customers", { params: { is_active: null } })
      .then((r) => setCustomers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditingCustomer(null);
    setForm({
      customer_number: "",
      name: "",
      address: "",
      city: "",
      route: "",
      region_id: "",
    });
    setError("");
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditingCustomer(c);
    setForm({
      customer_number: c.customer_number,
      name: c.name,
      address: c.address || "",
      city: c.city || "",
      route: c.route,
      region_id: c.region_id || "",
    });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setError("");

    try {
      if (editingCustomer) {
        await API.patch(`/admin/customers/${editingCustomer.id}`, form);
      } else {
        await API.post("/admin/customers", form);
      }
      setShowModal(false);
      fetchCustomers();
    } catch (err) {
      setError(parseError(err, "Failed to save customer."));
    }
  };

  const toggleActive = async (c) => {
    try {
      await API.patch(`/admin/customers/${c.id}/toggle-active`);
      fetchCustomers();
    } catch {}
  };

  const requestToggleActive = (c) => {
    setConfirmToggle({
      item: c,
      action: c.is_active ? "deactivate" : "activate",
    });
  };

  const availableRoutes = [...new Set(customers.map((c) => c.route))].sort();

  const filtered = customers.filter((c) => {
    if (showInactive && c.is_active) return false;
    if (!showInactive && !c.is_active) return false;
    if (routeFilter && c.route !== routeFilter) return false;
    if (regionFilter && c.region_id !== regionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !c.name.toLowerCase().includes(q) &&
        !c.customer_number.toLowerCase().includes(q) &&
        !(c.city || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    const av = String(a[sortField] ?? "").toLowerCase();
    const bv = String(b[sortField] ?? "").toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap flex-1">
          <div className="relative flex-1 min-w-48">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"
              />
            </svg>
            <input
              type="text"
              placeholder="Search name, number, city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputCls} pl-9`}
            />
          </div>
          <select
            value={routeFilter}
            onChange={(e) => setRouteFilter(e.target.value)}
            className={selectCls}
            style={{ width: "auto" }}
          >
            <option value="">All Routes</option>
            {availableRoutes.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          {isSuperAdmin && (
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className={selectCls}
              style={{ width: "auto" }}
            >
              <option value="">All Regions</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span style={{ color: showInactive ? Color.RED : undefined }}>
                Show deactivated
              </span>
            </label>
          )}
          {canImport && (
            <button
              onClick={() => setShowImport(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium border flex-shrink-0"
              style={{ borderColor: Color.NAVY, color: Color.NAVY }}
            >
              ↑ Import Excel
            </button>
          )}
          {
            <button
              onClick={openCreate}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex-shrink-0"
              style={{ backgroundColor: Color.NAVY }}
            >
              + Add Customer
            </button>
          }
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {[
                  { field: "customer_number", label: "#" },
                  { field: "name", label: "Name" },
                  { field: "address", label: "Address", hidden: true },
                  { field: "route", label: "Route" },
                  { field: "region_id", label: "Region", hidden: true },
                  { field: "is_active", label: "Status" },
                ].map((col) => (
                  <th
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                    className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none ${col.hidden ? "hidden md:table-cell" : ""}`}
                  >
                    {col.label}
                    {sortField === col.field
                      ? sortDir === "asc"
                        ? " ↑"
                        : " ↓"
                      : " ↕"}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {c.customer_number}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {c.name}
                    <p className="text-xs text-gray-400">{c.city}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {c.address}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {c.route}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {regions.find((r) => r.id === c.region_id)?.name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      label={c.is_active ? "Active" : "Inactive"}
                      bg={c.is_active ? "#E8F5E9" : "#F5F5F5"}
                      color={c.is_active ? "#005700" : "#666"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      {c.is_active && (
                        <ActionButton
                          label="Edit"
                          onClick={() => openEdit(c)}
                        />
                      )}
                      {c.is_active ? (
                        <ActionButton
                          label="Deactivate"
                          onClick={() => requestToggleActive(c)}
                          color={Color.RED}
                        />
                      ) : (
                        <ActionButton
                          label="Reactivate"
                          onClick={() => requestToggleActive(c)}
                          color={Color.GREEN}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingCustomer ? "Edit Customer" : "Add Customer"}
          onClose={() => setShowModal(false)}
        >
          <FormField label="Customer Number" required>
            <input
              className={inputCls}
              value={form.customer_number}
              onChange={(e) =>
                setForm((f) => ({ ...f, customer_number: e.target.value }))
              }
              disabled={!!editingCustomer}
            />
          </FormField>
          <FormField label="Store Name" required>
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </FormField>
          <FormField label="Address">
            <input
              className={inputCls}
              value={form.address}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
            />
          </FormField>
          <FormField label="City">
            <input
              className={inputCls}
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            />
          </FormField>
          <FormField label="Route" required>
            <input
              className={inputCls}
              placeholder="e.g. CCCC01"
              value={form.route}
              onChange={(e) =>
                setForm((f) => ({ ...f, route: e.target.value }))
              }
            />
          </FormField>
          {isSuperAdmin && (
            <FormField label="Region">
              <select
                className={selectCls}
                value={form.region_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, region_id: e.target.value }))
                }
              >
                <option value="">Select region...</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          {error && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ backgroundColor: Color.NAVY }}
            >
              {editingCustomer ? "Save Changes" : "Add Customer"}
            </button>
          </div>
        </Modal>
      )}
      {showImport && (
        <ImportModal
          type="customers"
          regionId={crossRegion ? null : undefined}
          mustPickRegion={crossRegion}
          regions={regions}
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            setShowImport(false);
            fetchCustomers();
          }}
        />
      )}

      {/* Deactivate / Reactivate confirmation modal */}
      {confirmToggle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{
                backgroundColor:
                  confirmToggle.action === "deactivate" ? "#FDECEA" : "#E8F5E9",
              }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  color:
                    confirmToggle.action === "deactivate"
                      ? "#CC0033"
                      : "#008000",
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    confirmToggle.action === "deactivate"
                      ? "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                      : "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  }
                />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">
              {confirmToggle.action === "deactivate"
                ? "Deactivate Customer?"
                : "Reactivate Customer?"}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {confirmToggle.action === "deactivate" ? (
                <>
                  Are you sure you want to deactivate{" "}
                  <strong>{confirmToggle.item.name}</strong>?
                </>
              ) : (
                <>
                  Are you sure you want to reactivate{" "}
                  <strong>{confirmToggle.item.name}</strong>?
                </>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmToggle(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await toggleActive(confirmToggle.item);
                  setConfirmToggle(null);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
                style={{
                  backgroundColor:
                    confirmToggle.action === "deactivate"
                      ? "#CC0033"
                      : "#008000",
                }}
              >
                {confirmToggle.action === "deactivate"
                  ? "Yes, Deactivate"
                  : "Yes, Reactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Categories Tab ────────────────────────────────────────────────────────────

function CategoriesTab({ isSuperAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newRequiresCooler, setNewRequiresCooler] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = () => {
    setLoading(true);
    API.get("/admin/categories")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setError("");
    try {
      await API.post("/admin/categories", {
        name: newName.trim(),
        requires_cooler: newRequiresCooler,
      });
      setNewName("");
      setNewRequiresCooler(false);
      fetchItems();
    } catch (err) {
      setError(parseError(err, "Failed to add category."));
    }
  };

  const toggleActive = async (item) => {
    try {
      await API.patch(`/admin/categories/${item.id}`, {
        is_active: !item.is_active,
      });
      fetchItems();
    } catch {}
  };

  const toggleRequiresCooler = async (item) => {
    try {
      await API.patch(`/admin/categories/${item.id}`, {
        requires_cooler: !item.requires_cooler,
      });
      fetchItems();
    } catch {}
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`))
      return;
    try {
      await API.delete(`/admin/categories/${item.id}`);
      fetchItems();
    } catch {
      alert("Failed to delete. It may be in use by existing work orders.");
    }
  };

  return (
    <div className="max-w-lg">
      <div className="flex gap-2 mb-2">
        <input
          className={`${inputCls} flex-1`}
          placeholder="New category name..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: Color.NAVY }}
        >
          Add
        </button>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600 mb-4 cursor-pointer">
        <input
          type="checkbox"
          className="rounded"
          checked={newRequiresCooler}
          onChange={(e) => setNewRequiresCooler(e.target.checked)}
        />
        This category involves a cooler
        <span className="text-gray-400">
          — asks for a cooler model when creating an order
        </span>
      </label>
      {error && (
        <div
          className="mb-3 p-3 rounded-lg text-sm"
          style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
        >
          {error}
        </div>
      )}
      {loading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {items.map((item, i) => (
            <div
              key={item.id}
              className={`flex items-center justify-between px-4 py-3 ${i < items.length - 1 ? "border-b border-gray-100" : ""}`}
            >
              <span
                className={`text-sm ${item.is_active ? "text-gray-800" : "text-gray-400 line-through"}`}
              >
                {item.name}
                {item.requires_cooler && (
                  <span
                    className="ml-2 text-xs px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: "#E3F4FB", color: "#0077A3" }}
                  >
                    Cooler
                  </span>
                )}
              </span>
              <div className="flex gap-1.5">
                <ActionButton
                  label={item.requires_cooler ? "No cooler" : "Needs cooler"}
                  onClick={() => toggleRequiresCooler(item)}
                  color={Color.LIGHT_BLUE}
                />
                <ActionButton
                  label={item.is_active ? "Deactivate" : "Activate"}
                  onClick={() => toggleActive(item)}
                  color={item.is_active ? Color.RED : Color.GREEN}
                />
                {isSuperAdmin && (
                  <ActionButton
                    label="Delete"
                    onClick={() => handleDelete(item)}
                    color={Color.RED}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Coolers Tab ───────────────────────────────────────────────────────────────

function CoolersTab({ isSuperAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = () => {
    setLoading(true);
    API.get("/admin/coolers")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setError("");
    try {
      await API.post("/admin/coolers", { name: newName.trim() });
      setNewName("");
      fetchItems();
    } catch (err) {
      setError(parseError(err, "Failed to add cooler model."));
    }
  };

  const toggleActive = async (item) => {
    try {
      await API.patch(`/admin/coolers/${item.id}`, {
        is_active: !item.is_active,
      });
      fetchItems();
    } catch {}
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`))
      return;
    try {
      await API.delete(`/admin/coolers/${item.id}`);
      fetchItems();
    } catch {
      alert("Failed to delete. It may be in use by existing work orders.");
    }
  };

  return (
    <div className="max-w-lg">
      <div className="flex gap-2 mb-4">
        <input
          className={`${inputCls} flex-1`}
          placeholder="New cooler model name..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: Color.NAVY }}
        >
          Add
        </button>
      </div>
      {error && (
        <div
          className="mb-3 p-3 rounded-lg text-sm"
          style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
        >
          {error}
        </div>
      )}
      {loading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {items.map((item, i) => (
            <div
              key={item.id}
              className={`flex items-center justify-between px-4 py-3 ${i < items.length - 1 ? "border-b border-gray-100" : ""}`}
            >
              <span
                className={`text-sm ${item.is_active ? "text-gray-800" : "text-gray-400 line-through"}`}
              >
                {item.name}
              </span>
              <div className="flex gap-1.5">
                <ActionButton
                  label={item.is_active ? "Deactivate" : "Activate"}
                  onClick={() => toggleActive(item)}
                  color={item.is_active ? Color.RED : Color.GREEN}
                />
                {isSuperAdmin && (
                  <ActionButton
                    label="Delete"
                    onClick={() => handleDelete(item)}
                    color={Color.RED}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Regions Tab (Super Admin only) ────────────────────────────────────────────

function RegionsTab() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", timezone: "", country: "CA" });

  const TIMEZONES = [
    "America/Toronto",
    "America/Vancouver",
    "America/Winnipeg",
    "America/Edmonton",
    "America/Halifax",
    "America/St_Johns",
    "America/Regina",
    "America/Whitehorse",
    "America/Yellowknife",
  ];

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = () => {
    setLoading(true);
    API.get("/admin/regions")
      .then((r) => setRegions(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditingRegion(null);
    setForm({ name: "", timezone: "America/Toronto", country: "CA" });
    setError("");
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditingRegion(r);
    setForm({ name: r.name, timezone: r.timezone, country: r.country });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.name.trim() || !form.timezone) {
      setError("Name and timezone are required.");
      return;
    }
    try {
      const payload = { ...form };
      if (editingRegion) {
        await API.patch(`/admin/regions/${editingRegion.id}`, payload);
      } else {
        await API.post("/admin/regions", payload);
      }
      setShowModal(false);
      fetchRegions();
    } catch (err) {
      setError(parseError(err, "Failed to save region."));
    }
  };

  const handleDelete = async (r) => {
    if (
      !window.confirm(
        `Delete region "${r.name}"? This will fail if any staff or customers are assigned to it.`,
      )
    )
      return;
    try {
      await API.delete(`/admin/regions/${r.id}`);
      fetchRegions();
    } catch {
      alert(
        "Failed to delete. Staff or customers may still be assigned to this region.",
      );
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex justify-end mb-4">
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: Color.NAVY }}
        >
          + Add Region
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">
                  Timezone
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Country
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {regions.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {r.name}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">
                    {r.timezone}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {r.country}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      label={r.is_active ? "Active" : "Inactive"}
                      bg={r.is_active ? "#E8F5E9" : "#F5F5F5"}
                      color={r.is_active ? "#005700" : "#666"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <ActionButton label="Edit" onClick={() => openEdit(r)} />
                      <ActionButton
                        label="Delete"
                        onClick={() => handleDelete(r)}
                        color={Color.RED}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {regions.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-gray-400 text-sm"
                  >
                    No regions yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editingRegion ? "Edit Region" : "Add Region"}
          onClose={() => setShowModal(false)}
        >
          <FormField label="Region Name" required>
            <input
              className={inputCls}
              placeholder="e.g. Ontario North"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </FormField>
          <FormField label="Timezone" required>
            <select
              className={selectCls}
              value={form.timezone}
              onChange={(e) =>
                setForm((f) => ({ ...f, timezone: e.target.value }))
              }
            >
              <option value="">Select timezone...</option>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Country">
            <input
              className={inputCls}
              value={form.country}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  country: e.target.value.toUpperCase(),
                }))
              }
              maxLength={2}
            />
            <p className="text-xs text-gray-400 mt-1">
              2-letter country code (e.g. CA, US)
            </p>
          </FormField>
          {error && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ backgroundColor: Color.NAVY }}
            >
              {editingRegion ? "Save Changes" : "Add Region"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Delegations Tab ───────────────────────────────────────────────────────────

function DelegationsTab({ currentUser }) {
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [asms, setAsms] = useState([]);
  const [dsms, setDsms] = useState([]);
  const [form, setForm] = useState({
    delegated_to_id: "",
    dsm_id: "",
    reason: "",
    expires_at: "",
  });

  useEffect(() => {
    fetchDelegations();
    API.get("/admin/staff", { params: { is_active: true } })
      .then((r) => {
        setAsms(r.data.filter((s) => s.role === Role.ASM));
        setDsms(r.data.filter((s) => s.role === Role.DSM));
      })
      .catch(() => {});
  }, []);

  const fetchDelegations = () => {
    setLoading(true);
    API.get("/admin/delegations", { params: { active_only: false } })
      .then((r) => setDelegations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleCreate = async () => {
    setError("");
    try {
      // Set expiry to end of selected day (23:59:59)
      await API.post("/admin/delegations", {
        ...form,
        expires_at: form.expires_at + "T23:59:59",
      });
      setShowModal(false);
      fetchDelegations();
    } catch (err) {
      setError(parseError(err, "Failed to create delegation."));
    }
  };

  const handleRevoke = async (id) => {
    try {
      await API.patch(`/admin/delegations/${id}/revoke`);
      fetchDelegations();
    } catch {}
  };

  const fmt = (d) =>
    d
      ? new Date(d + (d.includes("T") ? "Z" : "")).toLocaleDateString("en-CA", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => {
            setShowModal(true);
            setError("");
            setForm({
              delegated_to_id: "",
              dsm_id: "",
              reason: "",
              expires_at: "",
            });
          }}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: Color.NAVY }}
        >
          + Grant Delegation
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  ASM
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Covering DSM
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">
                  Granted By
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Expires
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {delegations.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {d.delegated_to_name}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{d.dsm_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                    {d.delegated_by_name}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {fmt(d.expires_at)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      label={d.is_active ? "Active" : "Inactive"}
                      bg={d.is_active ? "#E8F5E9" : "#F5F5F5"}
                      color={d.is_active ? "#005700" : "#666"}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {d.is_active && (
                      <ActionButton
                        label="Revoke"
                        onClick={() => handleRevoke(d.id)}
                        color={Color.RED}
                      />
                    )}
                  </td>
                </tr>
              ))}
              {delegations.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-gray-400 text-sm"
                  >
                    No delegations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal title="Grant DSM Delegation" onClose={() => setShowModal(false)}>
          <FormField label="Delegate To (ASM)" required>
            <select
              className={selectCls}
              value={form.delegated_to_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, delegated_to_id: e.target.value }))
              }
            >
              <option value="">Select ASM...</option>
              {asms.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Covering DSM" required>
            <select
              className={selectCls}
              value={form.dsm_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, dsm_id: e.target.value }))
              }
            >
              <option value="">Select DSM...</option>
              {dsms.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Expires At" required>
            <input
              type="date"
              className={inputCls}
              value={form.expires_at}
              onChange={(e) =>
                setForm((f) => ({ ...f, expires_at: e.target.value }))
              }
            />
          </FormField>
          <FormField label="Reason">
            <input
              className={inputCls}
              placeholder="Optional note..."
              value={form.reason}
              onChange={(e) =>
                setForm((f) => ({ ...f, reason: e.target.value }))
              }
            />
          </FormField>
          {error && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => setShowModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white"
              style={{ backgroundColor: Color.NAVY }}
            >
              Grant Delegation
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main AdminPanel ───────────────────────────────────────────────────────────

// ── Customer Locations Tab ────────────────────────────────────────────────────

function LocationsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [regions, setRegions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    route: "",
    region_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sortField, setSortField] = useState("customer_number");
  const [sortDir, setSortDir] = useState("asc");

  const fetchStatus = () => {
    setLoading(true);
    API.get("/admin/geocoding/status")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStatus();
    API.get("/lookup/regions")
      .then((r) => setRegions(r.data))
      .catch(() => {});
  }, []);

  const retry = async (c) => {
    setRetrying(c.id);
    try {
      await API.post(`/admin/geocoding/retry/${c.id}`);
      fetchStatus();
    } catch {
    } finally {
      setRetrying(null);
    }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      address: c.address || "",
      city: c.city || "",
      route: c.route || "",
      region_id: c.region_id || "",
    });
    setError("");
  };

  // Saving here also re-runs the lookup: correcting an address is the whole
  // reason to be on this page, so making the user press Retry afterwards
  // would just be a second step.
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await API.patch(`/admin/customers/${editing.id}`, form);
      await API.post(`/admin/geocoding/retry/${editing.id}`);
      setEditing(null);
      fetchStatus();
    } catch (err) {
      setError(parseError(err, "Failed to save customer."));
    } finally {
      setSaving(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await API.get("/admin/geocoding/export", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `customer-locations-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Please try again.");
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  if (loading) return <div className="text-gray-400 text-sm">Loading...</div>;
  if (!data) return null;

  const { counts, needs_attention: rows } = data;

  const sorted = [...rows].sort((a, b) => {
    const av = String(a[sortField] ?? "").toLowerCase();
    const bv = String(b[sortField] ?? "").toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const cards = [
    { label: "Exact location", value: counts.exact, color: Color.GREEN },
    { label: "Town only", value: counts.city, color: "#B38600" },
    { label: "Not found", value: counts.failed, color: Color.RED },
    { label: "Not looked up yet", value: counts.pending, color: Color.GRAY },
  ];

  const columns = [
    { field: "customer_number", label: "Customer #" },
    { field: "name", label: "Name" },
    { field: "address", label: "Address" },
    { field: "city", label: "City" },
    { field: "route", label: "Route" },
    { field: "region_name", label: "Warehouse" },
    { field: "precision", label: "Status" },
  ];

  const statusStyle = (p) =>
    p === "failed"
      ? { backgroundColor: "#FDECEA", color: Color.RED }
      : p === "pending"
        ? { backgroundColor: "#F5F5F5", color: "#666" }
        : { backgroundColor: "#FFF8E1", color: "#B38600" };

  const statusLabel = (p) =>
    p === "failed" ? "Not found" : p === "pending" ? "Not looked up" : "Town only";

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-gray-500 max-w-2xl">
          Work orders appear on the map using their customer&apos;s location.
          Customers below either could not be found, or matched only to their
          town — their orders show an approximate pin. Fix the address here and
          it is looked up again straight away.
        </p>
        {rows.length > 0 && (
          <button
            onClick={exportExcel}
            className="px-4 py-2 rounded-lg text-sm font-medium border flex-shrink-0"
            style={{ borderColor: Color.NAVY, color: Color.NAVY }}
          >
            ↓ Export Excel
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 max-w-3xl">
        {cards.map((c) => (
          <div
            key={c.label}
            className="bg-white rounded-xl border border-gray-200 p-4"
          >
            <div className="text-2xl font-bold" style={{ color: c.color }}>
              {c.value}
            </div>
            <div className="text-xs text-gray-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-gray-500">
          Every active customer has an exact location.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {columns.map((col) => (
                  <th
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500
                               uppercase tracking-wide cursor-pointer select-none
                               hover:text-gray-700 whitespace-nowrap"
                  >
                    {col.label}
                    {sortField === col.field ? (
                      <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>
                    ) : (
                      <span className="ml-1 text-gray-300">↕</span>
                    )}
                  </th>
                ))}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {c.customer_number}
                  </td>
                  <td className="px-4 py-3 text-gray-800">{c.name}</td>
                  <td className="px-4 py-3 text-gray-600">{c.address || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{c.route || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.region_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                      style={statusStyle(c.precision)}
                    >
                      {statusLabel(c.precision)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <ActionButton label="Edit" onClick={() => openEdit(c)} />
                      <ActionButton
                        label={retrying === c.id ? "..." : "Retry"}
                        onClick={() => retry(c)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal
          title={`Edit ${editing.customer_number}`}
          onClose={() => setEditing(null)}
        >
          {error && (
            <div
              className="mb-3 p-3 rounded-lg text-sm"
              style={{ backgroundColor: "#FDECEA", color: "#A00028" }}
            >
              {error}
            </div>
          )}
          <FormField label="Name" required>
            <input
              className={inputCls}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </FormField>
          <FormField label="Address">
            <input
              className={inputCls}
              value={form.address}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
            />
          </FormField>
          <FormField label="City">
            <input
              className={inputCls}
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            />
          </FormField>
          <FormField label="Route" required>
            <input
              className={inputCls}
              value={form.route}
              onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}
            />
          </FormField>
          <FormField label="Warehouse" required>
            <select
              className={selectCls}
              value={form.region_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, region_id: e.target.value }))
              }
            >
              <option value="">Select warehouse...</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </FormField>

          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setEditing(null)}
              className="flex-1 py-2 rounded-lg text-sm font-medium border
                         border-gray-300 text-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex-[2] py-2 rounded-lg text-sm font-semibold text-white
                         disabled:opacity-50"
              style={{ backgroundColor: Color.NAVY }}
            >
              {saving ? "Saving..." : "Save & find location"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const { user, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("staff");

  const isGSMOrAbove = isSuperAdmin() || user?.role === Role.GSM;

  // Users who cover every warehouse must name a target one when importing.
  const crossRegion =
    isSuperAdmin() ||
    [Role.ADMIN, Role.GSM].includes(user?.role) ||
    (user?.role === Role.DSM && !!user?.is_admin);

  // A DSM's panel covers staff and customers only.
  const isDsm = user?.role === Role.DSM;

  const tabs = [
    { key: "staff", label: "Staff" },
    { key: "customers", label: "Customers" },
    { key: "categories", label: "Task Categories" },
    ...(isDsm
      ? []
      : [
          { key: "coolers", label: "Cooler Models" },
        ]),
    // Customer Locations is a maintenance screen — Super Admin only
    ...(isSuperAdmin() ? [{ key: "locations", label: "Customer Locations" }] : []),
    // Delegations tab — GSM and Super Admin only (DSMs use the sidebar page)
    ...(isGSMOrAbove ? [{ key: "delegations", label: "Delegations" }] : []),
    // Regions tab — Super Admin only
    ...(isSuperAdmin() ? [{ key: "regions", label: "Regions" }] : []),
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
        <p className="text-gray-500 text-sm mt-1">
          Manage staff, customers, and system settings
        </p>
      </div>

      <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === "staff" && (
        <StaffTab
          isSuperAdmin={isSuperAdmin()}
          crossRegion={crossRegion}
          canImport={!isDsm}
          currentUser={user}
        />
      )}
      {activeTab === "customers" && (
        <CustomersTab
          isSuperAdmin={isSuperAdmin()}
          crossRegion={crossRegion}
          canImport={!isDsm}
        />
      )}
      {activeTab === "categories" && (
        <CategoriesTab isSuperAdmin={isSuperAdmin()} />
      )}
      {activeTab === "coolers" && <CoolersTab isSuperAdmin={isSuperAdmin()} />}
      {activeTab === "locations" && isSuperAdmin() && <LocationsTab />}
      {activeTab === "delegations" && <DelegationsTab currentUser={user} />}
      {activeTab === "regions" && isSuperAdmin() && <RegionsTab />}
    </div>
  );
}
